import React from 'react';

interface StoryProgressBarProps {
  completed: number;
  total: number;
  className?: string;
}

/**
 * Horizontal progress bar showing X / Y stories completed.
 * Color shifts: gray (0%), blue (1-50%), green (>50%).
 */
export const StoryProgressBar: React.FC<StoryProgressBarProps> = ({
  completed,
  total,
  className,
}) => {
  const safeTotal = Math.max(0, total);
  const safeCompleted = Math.max(0, Math.min(completed, safeTotal));
  const pct = safeTotal === 0 ? 0 : (safeCompleted / safeTotal) * 100;

  let barColor = 'bg-gray-300';
  if (pct > 0 && pct <= 50) barColor = 'bg-blue-500';
  else if (pct > 50) barColor = 'bg-green-500';

  const wrapperClasses = ['flex', 'items-center', 'gap-2'];
  if (className) wrapperClasses.push(className);

  return (
    <div className={wrapperClasses.join(' ')} aria-label={`${safeCompleted} of ${safeTotal} stories complete`}>
      <div className="relative h-2 w-full max-w-[120px] rounded-full bg-gray-200 overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 ${barColor}`}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={safeCompleted}
          aria-valuemin={0}
          aria-valuemax={safeTotal}
        />
      </div>
      <span className="text-xs tabular-nums text-gray-600">
        {safeCompleted}/{safeTotal}
      </span>
    </div>
  );
};
